import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { navigate } from '../router';
import { boxSizingStyles } from '../styles/lit-shared';

@customElement('architecture-diagram')
export class ArchitectureDiagram extends LitElement {
  private goTo(path: string) {
    void navigate(path);
  }

  static styles = [
    boxSizingStyles,
    css`
      :host {
        display: block;
      }

      .diagram {
        display: grid;
        gap: var(--space-md);
        padding: var(--space-lg);
        border-radius: var(--radius-lg);
        background: var(--color-surface);
        border: 1px solid var(--color-border);
        overflow-x: auto;
        margin-bottom: var(--space-xl);
      }

      .row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: center;
        gap: var(--space-sm);
      }

      .node {
        padding: 0.625rem 1rem;
        border-radius: var(--radius-sm);
        font-size: 0.8125rem;
        font-weight: 600;
        text-align: center;
        border: 1px solid var(--color-border);
        background: var(--color-surface-hover);
        min-width: 7rem;
      }

      .node--you {
        background: rgb(139 92 246 / 12%);
        border-color: rgb(139 92 246 / 30%);
        color: var(--color-primary-light);
      }

      .node--flow {
        background: rgb(6 182 212 / 12%);
        border-color: rgb(6 182 212 / 30%);
        color: var(--color-accent);
      }

      .node--team {
        background: var(--gradient-primary-soft);
        border-color: var(--color-border-glow);
        cursor: pointer;
        transition:
          border-color var(--duration-normal) var(--ease-out),
          transform var(--duration-normal) var(--ease-out),
          box-shadow var(--duration-normal) var(--ease-out);
      }

      .node--team:hover {
        border-color: var(--color-border-strong);
        transform: translateY(-2px);
        box-shadow: var(--shadow-glow-primary);
      }

      .node--deliverable {
        background: var(--color-surface-hover);
        font-size: 0.75rem;
        color: var(--color-text-muted);
      }

      .arrow {
        color: var(--color-text-subtle);
        font-size: 1.25rem;
        flex-shrink: 0;
      }

      .label {
        width: 100%;
        text-align: center;
        font-size: 0.6875rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: var(--color-text-subtle);
        margin-top: var(--space-xs);
      }

      .pipeline {
        display: grid;
        gap: var(--space-sm);
        margin-top: var(--space-md);
        padding-top: var(--space-md);
        border-top: 1px dashed var(--color-border);
      }

      .pipeline-title {
        font-size: 0.75rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--color-text-muted);
        text-align: center;
      }

      small {
        display: block;
        font-weight: 500;
        font-size: 0.6875rem;
        opacity: 0.85;
        margin-top: 0.125rem;
      }
    `,
  ];

  render() {
    return html`
      <div class="diagram" role="img" aria-label="Organigrama de Blunticle">
        <div class="row">
          <div class="node node--you">Tú + tu idea</div>
          <span class="arrow" aria-hidden="true">→</span>
          <div class="node node--flow">Recepción</div>
          <span class="arrow" aria-hidden="true">→</span>
          <div class="node node--flow">La oficina<br /><small>se pone a trabajar</small></div>
        </div>
        <p class="label">Tú no esperas mirando la pared — el equipo se activa</p>

        <div class="pipeline">
          <p class="pipeline-title">La cadena de mando</p>
          <div class="row">
            <div class="node node--team" @click=${() => this.goTo('/blunticle/agentes/director')} title="Ver El Director">Director<br /><small>entiende y reparte</small></div>
            <span class="arrow" aria-hidden="true">→</span>
            <div class="node node--team" @click=${() => this.goTo('/blunticle/agentes/writers')} title="Ver Los Writers">Redactores<br /><small>escriben a su rollo</small></div>
            <span class="arrow" aria-hidden="true">→</span>
            <div class="node node--team" @click=${() => this.goTo('/blunticle/agentes/secretario')} title="Ver El Secretario">Secretario<br /><small>revisa y pule</small></div>
            <span class="arrow" aria-hidden="true">→</span>
            <div class="node node--team" @click=${() => this.goTo('/blunticle/agentes/artista')} title="Ver El Artista">Artista<br /><small>la imagen</small></div>
          </div>
        </div>

        <div class="row" style="margin-top: var(--space-md)">
          <div class="node node--deliverable">Artículo</div>
          <div class="node node--deliverable">Posts redes</div>
          <div class="node node--deliverable">Imagen</div>
        </div>
        <p class="label">Lo que te llega: material listo para publicar. Aunque recomiendo revisar siempre el contenido, lo recibirás optimizado para SEO y GEO. Si tus clientes te encuentran a tarvés de él... ¡No hay excusas!</p>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'architecture-diagram': ArchitectureDiagram;
  }
}
